import { useEffect, useMemo, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { SelfServiceEmailLink } from "@/components/SelfServiceEmailLink";
import { HelperModeSwitch } from "@/components/HelperModeSwitch";
import { toast } from "sonner";
import { Check, Coffee, Power, Users, Sun, Moon } from "lucide-react";

// Iter186 — Player Self-Service.
// Frictionless mobile page reachable via a per-player token URL. No login.
// Coach shares the link from the Player Manager (Copy button, WhatsApp,
// SMS or QR). Also exposes an "Email me my link" button for players who
// only have the coach's marketing landing page in front of them.
const BACKEND = process.env.REACT_APP_BACKEND_URL;

const STATUS_META = {
  available: { label: "Available", cls: "bg-[#0F4B0F] border-[#4CAF50] text-[#CCFF00]", icon: <Check className="w-7 h-7" />, idle: "bg-[#141414] border-[#3F3F3F] text-white hover:border-[#4CAF50]" },
  resting: { label: "Resting", cls: "bg-[#0F2A4B] border-[#66C2FF] text-[#66C2FF]", icon: <Coffee className="w-7 h-7" />, idle: "bg-[#141414] border-[#3F3F3F] text-white hover:border-[#66C2FF]" },
  inactive: { label: "Done for now", cls: "bg-[#4B0F0F] border-[#FF4500] text-[#FF6B4B]", icon: <Power className="w-7 h-7" />, idle: "bg-[#2A0F0F] border-[#FF4500] text-[#FF8A6E] hover:bg-[#4B0F0F]" },
};

// Iter355 · Bug 4 — Friendly display labels for statuses that don't
// appear in the selector but still surface in the "Currently: …" line
// (coaching_locked, on_court, assigned, sin_bin, arrived, imported).
// Keeps the phone-facing string readable when the raw status is
// technical.
const STATUS_DISPLAY_LABEL = {
  available: "Available",
  arrived: "Available",
  resting: "Resting",
  inactive: "Done for now",
  coaching_locked: "Coaching — locked in",
  on_court: "On court",
  assigned: "Assigned to a court",
  sin_bin: "In the sin bin",
  imported: "Not checked in",
};

const BAND_CHIP = {
  // Iter270 — Green rating dot is dark green (`#166534`) for readable
  // contrast on the LIGHT theme. Prior lime `#CCFF00` was unreadable
  // against the ivory `#F7F5EF` background. Blue/red/spare unchanged.
  green: "bg-[#DCFCE7] text-[#166534] border-[#166534]",
  blue: "bg-[#0F2A4B] text-[#66C2FF] border-[#66C2FF]",
  red: "bg-[#4B0F0F] text-[#FF4500] border-[#FF4500]",
  spare: "bg-[#141414] text-[#FFB020] border-[#FFB020]",
};

function fmtMS(sec) {
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
}

export default function FestivalPlayerSelfService() {
  const { id, token } = useParams();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [tick, setTick] = useState(0);
  // Iter202 — verify key persists per phone / per player.
  const vkStorageKey = `rl_ss_vk_${id}_${token}`;
  const [verifyKey, setVerifyKey] = useState(() => {
    try { return window.localStorage.getItem(vkStorageKey) || ""; } catch { return ""; }
  });
  const [bookingInput, setBookingInput] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [verifyError, setVerifyError] = useState("");
  // Iter212 — Theme toggle. Persist choice per phone.
  const themeStorageKey = `rl_ss_theme_${id}`;
  const [theme, setTheme] = useState(() => {
    try { return window.localStorage.getItem(themeStorageKey) || "dark"; } catch { return "dark"; }
  });
  const toggleTheme = () => setTheme((t) => {
    const next = t === "dark" ? "light" : "dark";
    try { window.localStorage.setItem(themeStorageKey, next); } catch {}
    return next;
  });
  // Iter227 — "Add to Home Screen" prompt. Chrome/Android fire
  // `beforeinstallprompt`; we stash it and expose an install button.
  // iOS Safari never fires it, so we sniff standalone + iPhone and
  // show a "Tap Share → Add to Home Screen" hint instead.
  const [installEvent, setInstallEvent] = useState(null);
  const [installDismissed, setInstallDismissed] = useState(() => {
    try { return window.localStorage.getItem(`rl_ss_install_dismissed_${id}`) === "1"; } catch { return false; }
  });
  useEffect(() => {
    const onPrompt = (e) => { e.preventDefault(); setInstallEvent(e); };
    window.addEventListener("beforeinstallprompt", onPrompt);
    return () => window.removeEventListener("beforeinstallprompt", onPrompt);
  }, []);
  const isIOS = typeof navigator !== "undefined" && /iPad|iPhone|iPod/.test(navigator.userAgent);
  const isStandalone = typeof window !== "undefined" && (
    (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) ||
    (window.navigator && window.navigator.standalone === true)
  );
  const showInstallBanner = !installDismissed && !isStandalone && (installEvent || isIOS);
  const dismissInstall = () => {
    setInstallDismissed(true);
    try { window.localStorage.setItem(`rl_ss_install_dismissed_${id}`, "1"); } catch {}
  };
  const triggerInstall = async () => {
    if (!installEvent) return;
    try {
      installEvent.prompt();
      const { outcome } = await installEvent.userChoice;
      if (outcome === "accepted") toast.success("Installed! Rally-Lab now runs like an app.");
      setInstallEvent(null);
      dismissInstall();
    } catch {}
  };

  const [publicCourts, setPublicCourts] = useState([]);
  // Iter268 — Show/hide the ALL COURTS section (default visible, no persist).
  const [showCourts, setShowCourts] = useState(true);
  // Iter280 — Show/hide the Coaching Sessions section. Default visible;
  // the player toggles from the section header. Persisted per phone so
  // a returning player keeps their preference.
  const coachingVisibleKey = `rl_ss_coaching_visible_${id}`;
  const [showCoaching, setShowCoaching] = useState(() => {
    try { return window.localStorage.getItem(coachingVisibleKey) !== "0"; } catch { return true; }
  });
  const toggleCoaching = () => setShowCoaching((v) => {
    const next = !v;
    try { window.localStorage.setItem(coachingVisibleKey, next ? "1" : "0"); } catch {}
    return next;
  });
  // Iter280 — Persist which coaching groups the player has cancelled
  // out of so the "Cancelled" badge sticks until they re-register.
  const cancelledKey = `rl_ss_coaching_cancelled_${id}_${token}`;
  const [cancelledGroups, setCancelledGroups] = useState(() => {
    try {
      const raw = window.localStorage.getItem(cancelledKey);
      return raw ? JSON.parse(raw) : [];
    } catch { return []; }
  });
  const markGroupCancelled = (gid) => {
    if (!gid) return;
    setCancelledGroups((prev) => {
      const next = prev.includes(gid) ? prev : [...prev, gid];
      try { window.localStorage.setItem(cancelledKey, JSON.stringify(next)); } catch {}
      return next;
    });
  };
  const clearGroupCancelled = (gid) => {
    if (!gid) return;
    setCancelledGroups((prev) => {
      const next = prev.filter((g) => g !== gid);
      try { window.localStorage.setItem(cancelledKey, JSON.stringify(next)); } catch {}
      return next;
    });
  };
  // Iter268 — Queue-for-multiple-games selector. Default 1; the admin
  // can globally disable the feature (data.queue_multi_games_enabled).
  const [queueCount, setQueueCount] = useState(1);
  // Iter314 — Active play-request (as requester or target) drives the
  // GroupStatusBox that replaces the iter190-228 partner inbox +
  // welcome-back hero pattern. `null` = no active request → normal UX.
  const [activeRequest, setActiveRequest] = useState(null);

  // Iter319 — Graceful 402 on this player-facing page. Backend returns
  // 402 when Self-Service is disabled or the license doesn't include
  // it. We show a clean full-screen notice instead of a toast + stack
  // trace so players never see raw axios errors.
  const [serviceUnavailable, setServiceUnavailable] = useState(null);
  const load = async () => {
    try {
      const params = verifyKey ? `?vk=${encodeURIComponent(verifyKey)}` : "";
      // Iter320 — Preflight license status via the unauthenticated
      // /public payload before hitting the self-service endpoint.
      // This means the player never triggers a 402 in the first
      // place if the organiser hasn't paid for Self-Service.
      let pubData = null;
      try {
        const pub = await axios.get(`${BACKEND}/api/festival/${id}/public`, { __suppress402: true });
        pubData = pub?.data || null;
        setPublicCourts(pubData?.courts || []);
        if (pubData?.license && pubData.license.self_service_enabled === false) {
          setServiceUnavailable(
            "Self-Service isn't available for this event. Please head to the admin desk to check in."
          );
          return;
        }
      } catch { setPublicCourts([]); }
      // Iter320 — `__suppress402` opts out of the global toast so we
      // can render the full-screen graceful notice instead.
      const r = await axios.get(`${BACKEND}/api/festival/${id}/self-service/${token}${params}`, { __suppress402: true });
      if (r?.__handled402) {
        setServiceUnavailable(
          r.__detail || "Self-Service isn't available for this event. Please head to the admin desk to check in."
        );
        return;
      }
      // Iter263 — Fetch public courts payload in parallel so we can
      // render the coaching sign-up section for `reserved_coaching`
      // courts. Failure is silent (feature just doesn't render).
      // (Note: iter320 pre-fetches this before the self-service call
      // and reuses `pubData` above; we still refresh it here to keep
      // the courts data fresh on every poll.)
      try {
        const pub = await axios.get(`${BACKEND}/api/festival/${id}/public`, { __suppress402: true });
        setPublicCourts(pub?.data?.courts || []);
      } catch { setPublicCourts([]); }
      if (r.data?.needs_verification && verifyKey) {
        // Stored key rejected (rotated or reset by admin). Clear it.
        try { window.localStorage.removeItem(vkStorageKey); } catch {}
        setVerifyKey("");
      }
      // Iter225 — Invite toast + browser notification when a fresh
      // pending_invite appears (or when its court_id changes).
      try {
        const prev = (data && data.pending_invite) || null;
        const next = r.data?.pending_invite || null;
        const changed = next && (!prev || prev.court_id !== next.court_id);
        if (changed) {
          const courtName = next?.court_name || "your court";
          toast.success(`Court invite: ${courtName} — tap ACCEPT to hold your spot!`, { duration: 8000 });
          try {
            if ("Notification" in window && Notification.permission === "granted") {
              new Notification(`Rally-Lab: ${courtName}`, {
                body: "Tap ACCEPT to hold your spot — expires soon.",
                tag: `invite-${next.court_id}`,
              });
            }
          } catch {}
        }
      } catch {}
      setData(r.data);
      // Iter314 — Pull the player's active play-request (if any) so the
      // GroupStatusBox has fresh state on every poll. Silent 404s + 200s
      // with null both mean "no active request".
      try {
        const pr = await axios.get(
          `${BACKEND}/api/festival/${id}/self-service/${token}/play-requests/mine${verifyKey ? `?vk=${encodeURIComponent(verifyKey)}` : ""}`,
        );
        setActiveRequest(pr.data || null);
      } catch { setActiveRequest(null); }
    } catch (e) {
      // Iter319/322 — Any error on player-facing pages must degrade
      // gracefully. Never surface stack traces or raw axios errors.
      // 402 → Self-Service disabled. 404 → invalid festival or token.
      // Anything else → generic "link not recognised" screen.
      if (e?.response?.status === 402) {
        setServiceUnavailable(
          e?.response?.data?.detail ||
          "Self-Service isn't available for this event. Please head to the admin desk to check in."
        );
        return;
      }
      // Iter322 — 404 / 401 / network errors also render the graceful
      // screen so anonymous visitors never see a raw error page.
      setServiceUnavailable(
        e?.response?.data?.detail ||
        "This Self-Service link isn't valid — please ask your organiser for a fresh link, or check in at the admin desk."
      );
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id, token, verifyKey]);
  useEffect(() => {
    const t = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, []);
  // Iter225 — Ask for browser Notification permission on first render
  // (after the player has verified) so the invite toast can escape a
  // backgrounded tab.
  useEffect(() => {
    if (typeof window === "undefined" || !("Notification" in window)) return;
    if (Notification.permission === "default") {
      try { Notification.requestPermission().catch(() => {}); } catch {}
    }
  }, []);
  // Iter249 — Always-on background refresh so admin changes in Court
  // Ops surface on the player's phone without a manual reload. 5s
  // baseline; drops to 3s when a pending_invite is live (keeps the
  // countdown feeling real-time). Pauses while the tab is hidden and
  // triggers an immediate refresh when the tab regains focus, so
  // battery/data are only spent when the player is actually looking.
  useEffect(() => {
    if (!verifyKey) return; // still on the booking-id gate — no need to poll
    const intervalMs = data?.pending_invite?.court_id ? 3000 : 5000;
    let cancelled = false;
    const tick = () => {
      if (cancelled) return;
      if (typeof document !== "undefined" && document.hidden) return;
      load();
    };
    const t = setInterval(tick, intervalMs);
    const onVis = () => {
      if (typeof document !== "undefined" && !document.hidden) load();
    };
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("focus", onVis);
    return () => {
      cancelled = true;
      clearInterval(t);
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("focus", onVis);
    };
    // eslint-disable-next-line
  }, [verifyKey, data?.pending_invite?.court_id]);

  const submitVerify = async (e) => {
    e?.preventDefault?.();
    if (!bookingInput.trim() || verifying) return;
    setVerifying(true);
    setVerifyError("");
    try {
      // Iter444 — Send a stable device_id (same localStorage UUID
      // the helper-pass redeem uses) so the backend can enforce
      // single-device self-service: a verify from a new device
      // rotates the `verify_key` and the previously verified
      // device's cached vk goes stale on its next request.
      let deviceId = "";
      try {
        deviceId = window.localStorage.getItem("rl_device_id") || "";
        if (!deviceId) {
          deviceId = (window.crypto?.randomUUID?.() || Math.random().toString(36).slice(2));
          window.localStorage.setItem("rl_device_id", deviceId);
        }
      } catch { /* ignore */ }
      const r = await axios.post(
        `${BACKEND}/api/festival/${id}/self-service/${token}/verify`,
        { booking_id: bookingInput.trim(), device_id: deviceId || undefined },
      );
      const key = r.data?.verify_key;
      if (key) {
        try { window.localStorage.setItem(vkStorageKey, key); } catch {}
        setVerifyKey(key);
        setBookingInput("");
        toast.success("Verified — you're in");
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || "Couldn't verify";
      setVerifyError(detail);
      if (err?.response?.status === 423) {
        await load(); // pick up locked flag
      }
    } finally {
      setVerifying(false);
    }
  };

  const setStatus = async (status) => {
    if (busy) return;
    // Iter399 — Confirmation gate for the permanent Done-for-now
    // action. Once confirmed, the backend stamps `done_for_now=True`
    // and every self-service endpoint (including this one) returns
    // 423 until an admin manually reactivates them.
    if (status === "inactive") {
      const ok = typeof window !== "undefined" && window.confirm(
        "Are you sure? You will not be able to play again today. Only an admin can reinstate you."
      );
      if (!ok) return;
    }
    setBusy(true);
    try {
      // Iter268 — Pass queue_count when target is `available` AND the
      // admin has left the feature enabled on the festival. Backend
      // silently clamps to 1..4 and ignores when disabled.
      const body = { status };
      if (status === "available" && data?.queue_multi_games_enabled) {
        const maxGames = Math.max(1, Math.min(4, Number(data?.queue_multi_games_max) || 1));
        body.queue_count = Math.max(1, Math.min(maxGames, Number(queueCount) || 1));
      }
      await axios.post(
        `${BACKEND}/api/festival/${id}/self-service/${token}/status?vk=${encodeURIComponent(verifyKey)}`,
        body,
      );
      const suffix = (status === "available" && data?.queue_multi_games_enabled && body.queue_count > 1)
        ? ` · ${body.queue_count} games queued`
        : "";
      toast.success(`Marked ${STATUS_META[status]?.label || status}${suffix}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update");
    } finally { setBusy(false); }
  };

  const cooldownLeftInitial = data?.partner_cooldown_remaining_s || 0;
  const cooldownLeft = Math.max(0, cooldownLeftInitial - tick);

  if (!data) return (
    <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center p-6" data-testid="self-service-loading">
      {serviceUnavailable ? (
        <div className="w-full max-w-sm text-center" data-testid="ss-service-unavailable">
          <div className="text-[10px] uppercase tracking-widest text-[#FFB020]">Rally-Lab · Player</div>
          <h1 className="h-display text-2xl mt-2">Self-Service unavailable</h1>
          <p className="text-sm text-[#A3A3A3] mt-3 leading-relaxed">{serviceUnavailable}</p>
          <p className="text-[11px] text-[#525252] mt-4">
            No action needed — the admin desk can check you in and manage
            your court time directly.
          </p>
        </div>
      ) : (
        "Loading…"
      )}
    </div>
  );

  // Iter202 — Lock screen before verification.
  if (data.needs_verification) {
    const locked = data.verify_locked;
    const noBooking = !data.player?.has_booking_id;
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center p-6" data-testid="ss-verify-screen">
        <div className="w-full max-w-sm">
          <div className="text-[10px] uppercase tracking-widest text-[#CCFF00]">Rally-Lab · Player</div>
          <h1 className="h-display text-2xl mt-1">{data.festival?.name}</h1>
          <p className="text-sm text-[#A3A3A3] mt-1">
            Hi {data.player?.first_name || "there"} — quick check to confirm it's you.
          </p>
          {locked ? (
            <div className="mt-6 border border-[#FF4500] bg-[#4B0F0F]/40 p-4 text-sm" data-testid="ss-verify-locked">
              Too many wrong codes. Please pop over to the admin desk — they can reset it in seconds.
            </div>
          ) : noBooking ? (
            <div className="mt-6 border border-[#FFB020] bg-[#4B3F0F]/40 p-4 text-sm" data-testid="ss-verify-no-booking">
              No Booking ID on file yet. Please see the admin desk — they'll add one and you'll be in.
            </div>
          ) : (
            <form onSubmit={submitVerify} className="mt-6 space-y-3" data-testid="ss-verify-form">
              <label className="block text-[10px] uppercase tracking-wider text-[#525252]">Booking ID</label>
              <input
                data-testid="ss-verify-input"
                autoFocus
                value={bookingInput}
                onChange={(e) => setBookingInput(e.target.value)}
                placeholder="e.g. 1234Fest"
                className="w-full bg-[#141414] border border-[#262626] px-3 py-3 focus:outline-none focus:border-[#CCFF00]"
              />
              {verifyError && (
                <div className="text-xs text-[#FF4500]" data-testid="ss-verify-error">{verifyError}</div>
              )}
              <button
                type="submit"
                disabled={!bookingInput.trim() || verifying}
                data-testid="ss-verify-submit"
                className="w-full py-3 font-bold text-xs uppercase tracking-wider disabled:opacity-40"
                style={{ background: "#CCFF00", color: "#0A0A0A" }}
              >
                {verifying ? "Checking…" : "Unlock self-service"}
              </button>
              <div className="text-[10px] text-[#525252] leading-tight">
                Ask the desk if you don't remember your Booking ID. 5 wrong tries = temporary lock.
              </div>
            </form>
          )}
        </div>
      </div>
    );
  }

  const { player, festival, peers, partner_editing_enabled } = data;
  const checkedIn = data.checked_in !== false; // default true when field absent

  // Iter399 — Persistent "Done for now" lockout screen. When the
  // player has confirmed done_for_now, the entire self-service body
  // (status grid, partner picker, refresh, coaching signup, theme
  // toggle stays for readability) is replaced with a single inert
  // panel. No status changes, no partner requests, no refresh
  // escape — only an admin flipping their status in Player Manager /
  // Live Ops clears the flag (`update_player` handles the clear).
  if (player.done_for_now) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center p-6" data-testid="ss-done-for-day-lockout">
        <div className="w-full max-w-sm">
          <div className="text-[10px] uppercase tracking-widest text-[#FF6B4B]">Rally-Lab · Player</div>
          <h1 className="h-display text-2xl mt-1">{festival.name}</h1>
          <div className="text-sm text-[#A3A3A3] mt-1" data-testid="ss-done-for-day-name">
            {player.first_name} {player.last_name}
          </div>
          <div className="mt-8 border-2 border-[#FF4500] bg-[#4B0F0F] p-6" data-testid="ss-done-for-day-panel">
            <div className="text-xs uppercase tracking-widest text-[#FF6B4B] font-bold">Done for today</div>
            <div className="text-xl font-bold text-white mt-3 leading-tight">You are done for today.</div>
            <div className="text-sm text-[#FFB0A0] mt-4 leading-relaxed">
              See the admin desk if you want to play again.
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Iter400 — Sin-Bin lockout. Mirrors the Iter399 done_for_now
  // pattern: entire body replaced with a single inert panel that
  // shows the live countdown to the sin_bin_ends_at timestamp.
  // Auto-lifts when the timer expires (backend guard flips from 423
  // to 200 at the same moment; the frontend re-renders on the next
  // load() poll and the normal UI reappears). Admin manual release
  // is picked up on the same poll cadence.
  if (player.status === "sin_bin" && data.sin_bin_ends_at) {
    const endMs = new Date(data.sin_bin_ends_at).getTime();
    const nowMs = Date.now();
    const remaining = Math.max(0, Math.floor((endMs - nowMs) / 1000));
    if (remaining > 0) {
      const mm = String(Math.floor(remaining / 60)).padStart(2, "0");
      const ss = String(remaining % 60).padStart(2, "0");
      return (
        <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center p-6" data-testid="ss-sin-bin-lockout">
          <div className="w-full max-w-sm">
            <div className="text-[10px] uppercase tracking-widest text-[#FF6B4B]">Rally-Lab · Player</div>
            <h1 className="h-display text-2xl mt-1">{festival.name}</h1>
            <div className="text-sm text-[#A3A3A3] mt-1" data-testid="ss-sin-bin-name">
              {player.first_name} {player.last_name}
            </div>
            <div className="mt-8 border-2 border-[#FF4500] bg-[#4B0F0F] p-6" data-testid="ss-sin-bin-panel">
              <div className="text-xs uppercase tracking-widest text-[#FF6B4B] font-bold">Sin bin</div>
              <div className="text-xl font-bold text-white mt-3 leading-tight">You are in the sin bin.</div>
              <div className="text-sm text-[#FFB0A0] mt-4 leading-relaxed">
                You cannot play until the timer runs out or an admin releases you.
              </div>
              <div className="mt-6 border border-[#FF4500] bg-[#0A0A0A] px-4 py-3 text-center" data-testid="ss-sin-bin-countdown">
                <div className="text-[10px] uppercase tracking-widest text-[#FF6B4B]">Time remaining</div>
                <div className="text-3xl font-bold text-white tabular-nums mt-1">{mm}:{ss}</div>
              </div>
            </div>
          </div>
        </div>
      );
    }
  }

  // Iter212 — Theme toggle. Persist per phone. Default = dark (matches
  // existing feel) but the outdoor/sunny-court crowd can flip to light.
  const isLight = theme === "light";
  const ui = isLight
    ? {
        page: "bg-[#F7F7F5] text-[#0A0A0A]",
        muted: "text-[#525252]",
        subtleBorder: "border-[#D4D4D4]",
        cardBg: "bg-white",
        onCourt: "bg-[#0A0A0A] text-[#CCFF00] border-[#0A0A0A]",
        nextUp: "bg-white text-[#0A0A0A] border-[#66C2FF]",
        labelOnCourt: "text-[#CCFF00]",
        labelNextUp: "text-[#0F5FCC]",
        warnBox: "border-[#F1A300] bg-[#FFF3D6] text-[#4B3F0F]",
      }
    : {
        page: "bg-[#0A0A0A] text-white",
        muted: "text-[#A3A3A3]",
        subtleBorder: "border-[#262626]",
        cardBg: "bg-[#0F0F0A]",
        // High-contrast "on court" card: lime background, jet-black
        // heavy text — the readability fix from iter212.
        onCourt: "bg-[#CCFF00] text-black border-[#CCFF00]",
        nextUp: "bg-[#0F2A4B] text-white border-[#66C2FF]",
        labelOnCourt: "text-black",
        labelNextUp: "text-[#66C2FF]",
        warnBox: "border-[#FFB020] bg-[#4B3F0F]/40 text-white",
      };

  return (
    <div className={`min-h-screen ${ui.page} pb-16 transition-colors`} data-testid="festival-self-service" data-theme={theme}>
      <div className="max-w-md mx-auto p-4">
        <div className="flex items-center justify-between">
          <div className="text-[10px] uppercase tracking-widest text-[#CCFF00]">Rally-Lab · Player</div>
          <button
            type="button"
            onClick={toggleTheme}
            data-testid="ss-theme-toggle"
            className={`text-[10px] uppercase tracking-wider px-2 py-1 border ${ui.subtleBorder} hover:border-[#CCFF00] hover:text-[#CCFF00] inline-flex items-center gap-1`}
            aria-label="Toggle theme"
          >
            {isLight ? <Moon className="w-3 h-3" /> : <Sun className="w-3 h-3" />}
            {isLight ? "Dark" : "Light"}
          </button>
        </div>
        <h1 className="h-display text-2xl mt-1">{festival.name}</h1>
        <div className={`text-sm ${ui.muted}`}>
          {player.first_name} {player.last_name}
          {player.colour_band && (
            <span className={`ml-2 text-[9px] uppercase tracking-wider px-1.5 py-0.5 border ${BAND_CHIP[player.colour_band] || ui.subtleBorder}`}>
              {player.colour_band}
            </span>
          )}
        </div>

        {/* Iter313 — Header sponsor zone (mobile). */}
        {data.sponsor_ribbon && (
          <SponsorZoneMobile
            list={data.sponsor_ribbon.header || []}
            rotate={Math.max(3, Math.min(120, data.sponsor_ribbon.rotate_seconds || 60))}
            zone="header"
            ui={ui}
          />
        )}

        {/* Iter227 — Add-to-home-screen prompt so invite notifications
             still fire when the tab is closed. Chrome/Android use the
             native beforeinstallprompt; iOS gets a manual hint. */}
        {showInstallBanner && (
          <div className="mt-3 border border-[#66C2FF] bg-[#0F3A4B]/40 p-3 flex items-start gap-2" data-testid="ss-install-banner">
            <div className="flex-1">
              <div className="text-xs uppercase tracking-widest text-[#66C2FF] font-bold">Save to home screen</div>
              <div className="text-sm text-white mt-1">
                {isIOS && !installEvent
                  ? "Tap the Share icon → Add to Home Screen so you never miss a court invite."
                  : "One tap adds Rally-Lab to your home screen so we can ping you the second an invite arrives — even when this tab is closed."}
              </div>
              {installEvent && (
                <button
                  type="button"
                  data-testid="ss-install-btn"
                  onClick={triggerInstall}
                  className="mt-2 px-3 py-1.5 text-xs uppercase tracking-wider bg-[#66C2FF] text-black font-bold"
                >
                  Install app
                </button>
              )}
            </div>
            <button
              type="button"
              data-testid="ss-install-dismiss"
              onClick={dismissInstall}
              className="text-[#A3A3A3] hover:text-white text-lg leading-none"
              title="Dismiss"
            >
              ×
            </button>
          </div>
        )}

        {/* ================================================================
             ⚠️ Iter270 — COURT-INVITE CARD REMOVED PERMANENTLY.
             The only accept/decline prompt permitted in this component
             is `ss-partner-inbox` below (a DIRECT partner request from
             another named player). Do NOT add any other accept/decline
             prompts here. This was regressed three times (iter265/267/269).
             The `pending_invite` field on the payload is now legacy — the
             backend's `_stamp_invites` was neutered to a no-op in iter270.
             ================================================================ */}

        {/* Iter221 — Sin-bin countdown card. Blocks all interactions. */}
        {data.sin_bin_ends_at && player.status === "sin_bin" && (() => {
          const endMs = new Date(data.sin_bin_ends_at).getTime();
          const remaining = Math.max(0, Math.round((endMs - Date.now()) / 1000));
          return (
            <div className="mt-4 border-2 border-[#FF4500] bg-[#4B0F0F]/40 p-4 text-white" data-testid="ss-sin-bin-card">
              <div className="text-xs uppercase tracking-widest font-bold text-[#FF4500]">You're in the sin-bin</div>
              <div className="text-4xl font-extrabold leading-tight mt-1" data-testid="ss-sin-bin-remaining">
                {fmtMS(remaining)}
              </div>
              <div className="text-sm opacity-80 mt-1">
                Actions unlock automatically when the timer ends. See the admin desk if you think this is a mistake.
              </div>
            </div>
          );
        })()}

        {/* Iter263/280 — Coaching sign-up section. ALWAYS visible with a
            hide/show toggle. Groups multi-court sessions by
            `coaching_group_id` into a single card. Shows "Registered"
            or "Cancelled" state badges. */}
        <section className="mt-4 border border-[#CCFF00] bg-black p-3" data-testid="ss-coaching-section">
          <div className="flex items-center justify-between mb-2">
            <div className="label-mono text-[#CCFF00]">
              COACHING SESSIONS
              {(() => {
                // Iter338 · Bug 15 · Fix 2 follow-up — Visible count so
                // any future regression that clips the list is
                // immediately obvious in the DOM.
                // Iter358 · Testing follow-up — Match the queue-aware
                // filter used by the list body below so the count
                // reflects the number of rendered sessions (queue
                // entries count too, not just legacy top-level slots).
                const now = Date.now();
                const groupKeys = new Set();
                for (const cc of (publicCourts || [])) {
                  const queue = Array.isArray(cc.coaching_slot_queue) ? cc.coaching_slot_queue : [];
                  const flat = [...queue];
                  const queueGids = new Set(queue.map((q) => q.coaching_group_id).filter(Boolean));
                  if (cc.coaching_slot_at && (!cc.coaching_group_id || !queueGids.has(cc.coaching_group_id))) {
                    flat.push({
                      coaching_group_id: cc.coaching_group_id,
                      coaching_slot_at: cc.coaching_slot_at,
                      coaching_duration_minutes: cc.coaching_duration_minutes,
                    });
                  }
                  for (const slot of flat) {
                    if (!slot.coaching_slot_at) continue;
                    try {
                      const start = new Date(slot.coaching_slot_at).getTime();
                      const dur = (slot.coaching_duration_minutes || 60) * 60000;
                      if ((start + dur) <= now - 60000) continue;
                    } catch { continue; }
                    groupKeys.add(slot.coaching_group_id || `solo::${cc.id}::${slot.coaching_slot_at}`);
                  }
                }
                return <span className={`${ui.muted} ml-2 text-[10px] normal-case`} data-testid="ss-coaching-count">· {groupKeys.size} session{groupKeys.size === 1 ? "" : "s"}</span>;
              })()}
            </div>
            <button type="button" onClick={toggleCoaching} data-testid="ss-coaching-toggle" className={`text-[9px] px-2 py-0.5 border ${ui.subtleBorder} ${ui.muted} uppercase tracking-wider hover:border-[#CCFF00] hover:text-[#CCFF00]`}>
              {showCoaching ? "Hide" : "Show"}
            </button>
          </div>
          {showCoaching && (() => {
            // Iter280/284/338/341 — Group scheduled coaching sessions
            // by coaching_group_id. Sessions are ALWAYS visible from
            // the moment they're created (regardless of registration
            // window or T-10 flip) — a filter drops finished sessions
            // whose end time has passed. Iter341 · Bug 16a rerun —
            // Absolutely no cap: every court that satisfies the
            // filter is included, deduped only when they share a
            // coaching_group_id, else grouped by court id.
            const now = Date.now();
            // Iter358 · Bug 2 — Flatten every court's coaching_slot_queue
            // (plus legacy top-level slot when the queue is empty)
            // so players see EVERY upcoming session, including ones
            // still hours away that only live in the queue post-
            // Iter357. Filter drops slots whose full window has ended.
            const coachingCourts = [];
            const seenSessionKeys = new Set();
            for (const cc of (publicCourts || [])) {
              const queue = Array.isArray(cc.coaching_slot_queue) ? cc.coaching_slot_queue : [];
              const flat = [...queue];
              const queueGids = new Set(queue.map((q) => q.coaching_group_id).filter(Boolean));
              if (cc.coaching_slot_at && (!cc.coaching_group_id || !queueGids.has(cc.coaching_group_id))) {
                flat.push({
                  coaching_group_id: cc.coaching_group_id,
                  coaching_session_name: cc.coaching_session_name,
                  coaching_coach_name: cc.coaching_coach_name,
                  coaching_level: cc.coaching_level,
                  coaching_slot_at: cc.coaching_slot_at,
                  coaching_duration_minutes: cc.coaching_duration_minutes,
                  coaching_max_players: cc.coaching_max_players,
                  coaching_signup_ids: cc.coaching_signup_ids || [],
                  coaching_registration_opens_at: cc.coaching_registration_opens_at,
                  coaching_registration_forced_open_at: cc.coaching_registration_forced_open_at,
                });
              }
              for (const slot of flat) {
                if (!slot.coaching_slot_at) continue;
                try {
                  const startMs = new Date(slot.coaching_slot_at).getTime();
                  const durMs = (slot.coaching_duration_minutes || 60) * 60000;
                  if ((startMs + durMs) <= now) continue;
                } catch { continue; }
                const merged = {
                  ...cc,
                  coaching_group_id: slot.coaching_group_id,
                  coaching_session_name: slot.coaching_session_name,
                  coaching_coach_name: slot.coaching_coach_name,
                  coaching_level: slot.coaching_level,
                  coaching_slot_at: slot.coaching_slot_at,
                  coaching_duration_minutes: slot.coaching_duration_minutes,
                  coaching_max_players: slot.coaching_max_players,
                  coaching_signup_ids: slot.coaching_signup_ids || [],
                  coaching_registration_opens_at: slot.coaching_registration_opens_at,
                  coaching_registration_forced_open_at: slot.coaching_registration_forced_open_at,
                };
                const dedupKey = slot.coaching_group_id
                  ? `gid::${slot.coaching_group_id}::${cc.id}`
                  : `solo::${cc.id}::${slot.coaching_slot_at}`;
                if (seenSessionKeys.has(dedupKey)) continue;
                seenSessionKeys.add(dedupKey);
                coachingCourts.push(merged);
              }
            }
            if (coachingCourts.length === 0) {
              return (
                <div className={`text-[11px] ${ui.muted} italic`} data-testid="ss-coaching-empty">
                  No coaching sessions scheduled yet. Check back later.
                </div>
              );
            }
            const groups = new Map();
            for (const cc of coachingCourts) {
              const key = cc.coaching_group_id || `solo::${cc.id}`;
              if (!groups.has(key)) groups.set(key, []);
              groups.get(key).push(cc);
            }
            const rows = Array.from(groups.entries()).map(([key, list]) => {
              list.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
              return { key, anchor: list[0], courts: list };
            }).sort((a, b) => new Date(a.anchor.coaching_slot_at || 0) - new Date(b.anchor.coaching_slot_at || 0));
            return (
              <div className="space-y-2 max-h-[70vh] overflow-y-auto pr-1" data-testid="ss-coaching-list">
                {rows.map(({ key, anchor: cc, courts: linked }) => {
                  const gid = cc.coaching_group_id || key;
                  const totalCap = linked.reduce((sum, x) => sum + (x.coaching_max_players || 6), 0);
                  const totalSignups = linked.reduce((sum, x) => sum + ((x.coaching_signup_ids || []).length), 0);
                  const spaces = totalCap - totalSignups;
                  // Iter280 — Signed up on ANY court in the group?
                  const signedUpCourt = linked.find((x) => (x.coaching_signup_ids || []).includes(data.player?.id));
                  const signedUp = !!signedUpCourt;
                  const wasCancelled = !signedUp && cancelledGroups.includes(gid);
                  // Sync "cancelled" flag with reality — if the player
                  // is now signed up we clear the cancelled marker.
                  if (signedUp && cancelledGroups.includes(gid)) {
                    setTimeout(() => clearGroupCancelled(gid), 0);
                  }
                  const inQueue = !!(data.me_on_court || data.me_next_up || data.player?.status === "available");
                  const forcedOpen = !!cc.coaching_registration_forced_open_at;
                  const opensAtMs = cc.coaching_registration_opens_at ? new Date(cc.coaching_registration_opens_at).getTime() : 0;
                  const regOpen = forcedOpen || !opensAtMs || Date.now() >= opensAtMs;
                  const regCountdownS = regOpen ? 0 : Math.max(0, Math.round((opensAtMs - Date.now()) / 1000));
                  const band = (data.player?.colour_band || "").toLowerCase();
                  const levelOk = !cc.coaching_level
                    || (cc.coaching_level === "u35" && (band === "blue" || band === "green"))
                    || (cc.coaching_level === "o35" && (band === "blue" || band === "red"));
                  const onSignup = async () => {
                    if (inQueue) {
                      const ok = window.confirm("You are currently in the match queue. Signing up for coaching will not remove you from the queue. Do you want to continue?");
                      if (!ok) return;
                    }
                    try {
                      // Iter280 — Multi-court aware: hit the anchor court
                      // endpoint; the backend routes to the least-full
                      // court in the group.
                      await axios.post(`${BACKEND}/api/festival/${id}/self-service/${token}/coaching/${cc.id}/signup?gid=${encodeURIComponent(gid)}${verifyKey ? `&vk=${encodeURIComponent(verifyKey)}` : ""}`);
                      clearGroupCancelled(gid);
                      toast.success("Signed up for coaching");
                      load();
                    } catch (e) { toast.error(e?.response?.data?.detail || "Sign-up failed"); }
                  };
                  const onCancel = async () => {
                    const targetId = signedUpCourt?.id || cc.id;
                    try {
                      await axios.post(`${BACKEND}/api/festival/${id}/self-service/${token}/coaching/${targetId}/cancel?gid=${encodeURIComponent(gid)}${verifyKey ? `&vk=${encodeURIComponent(verifyKey)}` : ""}`);
                      markGroupCancelled(gid);
                      toast.success("Removed from coaching");
                      load();
                    } catch (e) { toast.error("Failed"); }
                  };
                  const sessionLabel = cc.coaching_session_name || cc.name;
                  const startDisplay = cc.coaching_slot_at
                    ? new Date(cc.coaching_slot_at).toLocaleString([], { hour: "2-digit", minute: "2-digit", weekday: "short" })
                    : "Time TBC";
                  const courtsLabel = linked.length > 1
                    ? `${linked.length} courts: ${linked.map((x) => x.name).join(" + ")}`
                    : cc.name;
                  return (
                    <div key={key} data-testid={`ss-coaching-court-${cc.id}`} className={`border ${ui.subtleBorder} p-2 ${isLight ? "text-[#0A0A0A]" : "text-white"} text-sm`}>
                      {signedUp && (
                        <div className="mb-1 text-[10px] uppercase tracking-wider text-[#CCFF00] font-bold" data-testid={`ss-coaching-registered-${cc.id}`}>
                          ✓ REGISTERED · {signedUpCourt?.name || cc.name} · {startDisplay}
                        </div>
                      )}
                      {wasCancelled && (
                        <div className="mb-1 text-[10px] uppercase tracking-wider text-[#FFB020] font-bold" data-testid={`ss-coaching-cancelled-${cc.id}`}>
                          ✕ CANCELLED · You unregistered from this session
                        </div>
                      )}
                      <div className="flex items-center justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <div className="font-bold truncate">{sessionLabel}{cc.coaching_level ? ` · ${cc.coaching_level === "u35" ? "Under 3.5" : "Over 3.5"}` : ""}</div>
                          <div className={`text-[10px] ${ui.muted}`}>
                            {courtsLabel} · {startDisplay} · {spaces > 0 ? `${spaces} space(s) left` : "Full"}
                          </div>
                          {cc.coaching_coach_name && (
                            <div className={`text-[10px] ${ui.muted}`}>Coach: {cc.coaching_coach_name}</div>
                          )}
                        </div>
                        {signedUp ? (
                          (() => {
                            // Iter337 · Bug 15 · Fix 1 — Pre-session
                            // cancellation lock.
                            // Iter354 · Bug 1 — Strict 20-min boundary
                            // (< 20 min = < 1200000 ms), matching the
                            // backend coaching_locked window. A session
                            // 21+ min away is NOT locked; player can
                            // still cancel themselves.
                            const slotMs = cc.coaching_slot_at ? new Date(cc.coaching_slot_at).getTime() : 0;
                            const cancelLocked = slotMs > 0 && (slotMs - Date.now()) < 1200000;
                            if (cancelLocked) {
                              return (
                                <span
                                  data-testid={`ss-coaching-locked-in-${cc.id}`}
                                  className="text-[10px] text-[#FFB020] uppercase tracking-wider text-right max-w-[55%] leading-tight font-bold"
                                >
                                  You are locked in — see OPS or your organiser to make changes.
                                </span>
                              );
                            }
                            return (
                              <button type="button" onClick={onCancel} data-testid={`ss-coaching-cancel-${cc.id}`} className="text-[10px] px-3 py-1 border border-[#FFB020] text-[#FFB020] uppercase tracking-widest">Cancel</button>
                            );
                          })()
                        ) : !levelOk ? (
                          <span className="text-[10px] text-[#FFB020] uppercase tracking-wider text-right max-w-[45%] leading-tight" data-testid={`ss-coaching-blocked-${cc.id}`}>
                            Visit admin desk to request a place
                          </span>
                        ) : !regOpen ? (
                          (() => {
                            const slotMs = cc.coaching_slot_at ? new Date(cc.coaching_slot_at).getTime() : 0;
                            const withinLock = slotMs && (slotMs - Date.now()) <= 600000;
                            if (withinLock) {
                              return (
                                <span className="text-[10px] text-[#FFB020] uppercase tracking-wider text-right max-w-[50%] leading-tight" data-testid={`ss-coaching-locked-${cc.id}`}>
                                  Registration is now closed. Please visit the OPS desk to request a late place.
                                </span>
                              );
                            }
                            return (
                              <span className="text-[10px] font-mono text-[#FFB020] tabular-nums text-right" data-testid={`ss-coaching-countdown-${cc.id}`}>
                                Opens in {fmtMS(regCountdownS)}
                              </span>
                            );
                          })()
                        ) : (
                          <button type="button" disabled={spaces <= 0} onClick={onSignup} data-testid={`ss-coaching-signup-${gid}`} className="text-[10px] px-3 py-1 bg-[#CCFF00] text-black font-bold uppercase tracking-widest disabled:bg-[#262626] disabled:text-[#525252]">
                            {spaces <= 0 ? "Full" : "Register"}
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })()}
        </section>

        {/* Iter281 — Coaching fallback notice banner. Shown once when
             the auto-allocator seated the player on a coaching court
             because every other court was full. Dismiss clears the
             notice server-side. */}
        {data.coaching_fallback_notice && (
          <div className={`mt-4 border-2 border-[#66C2FF] ${isLight ? "bg-[#E8F1FF] text-[#0A0A0A]" : "bg-[#0F2A4B]/50 text-white"} p-3`} data-testid="ss-coaching-fallback-notice">
            <div className="text-[10px] uppercase tracking-widest text-[#66C2FF] font-bold">Coaching fallback</div>
            <div className="text-sm mt-1">{data.coaching_fallback_notice}</div>
            <button type="button" onClick={async () => {
              try {
                await axios.post(`${BACKEND}/api/festival/${id}/self-service/${token}/coaching/dismiss-notice${verifyKey ? `?vk=${encodeURIComponent(verifyKey)}` : ""}`);
                load();
              } catch {}
            }} data-testid="ss-fallback-dismiss" className="mt-2 text-[10px] px-3 py-1 border border-[#66C2FF] text-[#66C2FF] uppercase tracking-widest">Got it</button>
          </div>
        )}

        {/* Iter221/266/268 — YOUR TEAM banner. Per-member LIVE dot +
             an unlink X button so the player can clear a link
             themselves. */}
        {data.party && data.party.members && data.party.members.length > 0 && (
          <section className={`mt-4 border border-[#FFB020] ${isLight ? "bg-[#FFF3D6]" : "bg-[#4B3F0F]/40"} p-3`} data-testid="ss-party-banner">
            <div className="label-mono text-[#FFB020] mb-1">YOUR TEAM · {data.party.size} of 4</div>
            <ul className={`text-sm ${isLight ? "text-[#0A0A0A]" : "text-white"} space-y-0.5 mt-1`}>
              {data.party.members.map((m) => {
                const s = (m.status || "").toLowerCase();
                const ready = s === "available";
                const playing = s === "on_court" || s === "assigned";
                const dot = ready ? "text-[#CCFF00]" : playing ? "text-[#FF4500]" : "text-[#A3A3A3]";
                const label = ready ? "Available" : playing ? "Playing" : (s === "resting" ? "Resting" : (s === "arrived" ? "Checked-In" : (s || "—")));
                return (
                  <li key={m.id} className="flex items-center gap-2" data-testid={`ss-party-mate-${m.id}`}>
                    <span className={`text-lg leading-none ${dot}`}>●</span>
                    <span className="flex-1 truncate">{m.name}</span>
                    <span className={`text-[10px] uppercase tracking-wider ${dot}`}>{label}</span>
                    <button
                      type="button"
                      data-testid={`ss-unlink-${m.id}`}
                      title="Unlink"
                      onClick={async () => {
                        if (!window.confirm(`Unlink ${m.name}? This clears the team link for both of you.`)) return;
                        try {
                          await axios.post(`${BACKEND}/api/festival/${id}/self-service/${token}/partners/unlink${verifyKey ? `?vk=${encodeURIComponent(verifyKey)}` : ""}`, { partner_id: m.id });
                          toast.success(`Unlinked ${m.name}`);
                          load();
                        } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't unlink"); }
                      }}
                      className="text-[10px] leading-none px-1.5 py-0.5 border border-[#FF4500] text-[#FF4500] hover:bg-[#FF4500] hover:text-black"
                    >✕</button>
                  </li>
                );
              })}
            </ul>
            <div className={`text-[10px] ${ui.muted} mt-2`}>
              You'll all land on the same court together as soon as every team member is Available.
            </div>
          </section>
        )}

        {/* Iter276 — Holding-for-coaching banner. When a player is
             registered on a coaching court whose slot is within 20 min,
             they're held out of match allocation and see this banner
             instead of their normal queue actions. */}
        {/* Iter366 · Fix 2 — Combined coaching status card. The old
             yellow HOLDING FOR COACHING banner + blue COACHING · LOCKED
             IN card at the bottom were two views of the same state.
             They're now one card showing: court, session, coach, level,
             start time + countdown, and fellow players. Body text is
             text-base (16px) minimum, names never truncate, contrast
             is high in both themes. */}
        {data.holding_for_coaching && (() => {
          const h = data.holding_for_coaching;
          const conflict = !!h.active_game_conflict;
          const startMs = h.starts_at ? new Date(h.starts_at).getTime() : 0;
          const secsLeft = startMs > 0 ? Math.max(0, Math.round((startMs - Date.now()) / 1000)) : 0;
          const minsLeft = Math.max(0, Math.ceil(secsLeft / 60));
          const startLabel = startMs > 0 ? new Date(startMs).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "";
          const mates = h.signup_mates || [];
          return (
            <div
              className={`mt-4 border-2 p-4 space-y-3 ${
                conflict
                  ? "border-[#FF4500] bg-[#4B0F0F]/60"
                  : (isLight ? "border-[#66C2FF] bg-white text-[#0A0A0A]" : "border-[#66C2FF] bg-[#0F2A4B]")
              }`}
              data-testid="ss-holding-banner"
            >
              <div className={`text-xs uppercase tracking-widest font-bold ${conflict ? "text-[#FF4500]" : "text-[#66C2FF]"}`}>
                {conflict ? "Coaching Conflict" : "You're on the coaching list"}
              </div>
              <div className="text-2xl font-extrabold leading-tight" data-testid="ss-holding-title">
                COACHING · {h.court_name || "COURT"} IN {minsLeft} MIN
              </div>
              <div className="text-base leading-relaxed space-y-1" data-testid="ss-holding-meta">
                {h.session_name && (
                  <div className="font-bold" data-testid="ss-holding-session">{h.session_name}</div>
                )}
                {h.coach_name && <div>Coach <b>{h.coach_name}</b></div>}
                {h.level_label && <div>Level <b>{h.level_label}</b></div>}
                {startLabel && (
                  <div>
                    Starts <b>{startLabel}</b>
                    {secsLeft > 0 && <> · in <b data-testid="ss-holding-countdown">{fmtMS(secsLeft)}</b></>}
                  </div>
                )}
              </div>
              {mates.length > 0 && (
                <div>
                  <div className={`text-sm font-bold uppercase tracking-wider ${isLight ? "text-[#0A0A0A]" : "text-[#66C2FF]"} mb-1`}>Fellow players</div>
                  <ul className="space-y-1.5" data-testid="ss-holding-mates">
                    {mates.map((mt) => {
                      const band = (mt.colour_band || "").toLowerCase();
                      const dotCls =
                        band === "green" ? "text-[#166534]" :
                        band === "blue"  ? "text-[#66C2FF]" :
                        band === "red"   ? "text-[#FF4500]" :
                        band === "spare" ? "text-[#FFB020]" :
                        (isLight ? "text-[#525252]" : "text-[#A3A3A3]");
                      return (
                        <li key={mt.id} className="flex items-start gap-2 text-base leading-relaxed" data-testid={`ss-holding-mate-${mt.id}`}>
                          <span className={`text-xl leading-none ${dotCls} shrink-0`} title={band ? `Rating band ${band.toUpperCase()}` : "Unrated"}>●</span>
                          <span className="font-semibold break-words">{mt.name}</span>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
              {conflict && (
                <div className="text-base text-[#FF4500] font-semibold leading-relaxed">
                  Your current game overruns into the coaching start — head to court now.
                </div>
              )}
              {!conflict && (
                <div className={`text-sm ${isLight ? "text-[#525252]" : "text-white/80"}`}>
                  You're paused from match allocation until the session ends.
                </div>
              )}
            </div>
          );
        })()}

        {/* Iter190/212 — Personal status card. High-contrast lime block
            for "You're on" so the player can read it from across a hall.
            Iter268 — When on court, list the other 3 players' names
            with live-status dots to the right of the court number. */}
        {/* Iter356 · Bug 2 UI — Drag-drop coaching_locked players are
             ALSO on the court (current_player_ids), so `me_on_court`
             is truthy. Prefer the coaching-locked info panel whenever
             `holding_for_coaching` is present so those players see
             session name, coach, countdown, and mate list. Regular
             (non-coaching) on-court and next-up players still see
             the standard status card. */}
        {/* Iter358 · Bug 3 — Right-size the on-court / next-up banner.
             Previously the coaching context rendered a full-bleed
             lime-yellow card with text-4xl labels that overwhelmed
             the screen. Coaching-context uses the muted `nextUp`
             (blue) styling; non-coaching (regular match) stays with
             the bright on-court palette but at a smaller size. */}
        {(data.me_on_court || data.me_next_up) && !data.holding_for_coaching && (() => {
          const isCoachingCtx = !!(data.me_on_court?.is_coaching || data.me_next_up?.is_coaching);
          const cardCls = data.me_on_court && !isCoachingCtx ? ui.onCourt : ui.nextUp;
          const labelCls = data.me_on_court && !isCoachingCtx ? ui.labelOnCourt : ui.labelNextUp;
          return (
          <div className={`mt-4 border-2 p-3 ${cardCls}`} data-testid="ss-my-status-card">{data.me_on_court ? (
              <>
                <div className={`text-xs uppercase tracking-widest font-bold ${labelCls}`} data-testid="ss-on-court-label">You're on</div>
                {/* Iter271 — Court number on the LEFT, mate names in a
                     COLUMN on the RIGHT. Bigger text, dots still clearly
                     visible next to each name. Compact overall banner.
                     Iter339 · Bug 16b — When the player is on a coaching
                     court, replace the court label with "COACHING"
                     (or "COACHING · NEXT UP" when no further coaching
                     session is scheduled after this one).
                     Iter358 · Bug 3 — text-2xl for coaching context
                     so the banner is compact and doesn't dominate. */}
                <div className="flex items-start gap-3 mt-1">
                  <div className={`${isCoachingCtx ? "text-xl" : "text-3xl"} font-extrabold leading-tight shrink-0`} data-testid="ss-on-court-label-text">
                    {data.me_on_court?.is_coaching
                      ? (data.me_on_court?.has_further_coaching
                          ? `COACHING · ${data.me_on_court?.court_name || "COURT"}`
                          : `COACHING · NEXT UP · ${data.me_on_court?.court_name || "COURT"}`)
                      : (data.me_on_court?.court_name || "Court")}
                  </div>
                  {(data.me_on_court?.mates || []).length > 0 && (
                    <ul className="flex flex-col gap-1 min-w-0 flex-1" data-testid="ss-on-court-mates">
                      {(data.me_on_court.mates || []).map((mt) => {
                        // Iter269/270 — Dot colour = rating band (dark
                        // green / blue / red / spare). Availability
                        // status is deliberately NOT used here.
                        const band = (mt.colour_band || "").toLowerCase();
                        const dotCls =
                          band === "green" ? "text-[#166534]" :
                          band === "blue"  ? "text-[#66C2FF]" :
                          band === "red"   ? "text-[#FF4500]" :
                          band === "spare" ? "text-[#FFB020]" :
                          "text-[#A3A3A3]";
                        return (
                          <li key={mt.id} className="flex items-center gap-2 text-sm leading-tight" data-testid={`ss-on-court-mate-${mt.id}`}>
                            <span className={`text-lg leading-none ${dotCls}`} title={band ? `Rating band ${band.toUpperCase()}` : "Unrated"}>●</span>
                            <span className="font-semibold truncate">{mt.name}</span>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
                <div className="text-xs font-semibold mt-1 opacity-90">
                  {data.me_on_court?.state === "live" ? `${fmtMS(data.me_on_court?.remaining_seconds || 0)} left` : "Getting ready to start"}
                </div>
              </>
            ) : (
              <>
                <div className={`text-xs uppercase tracking-widest font-bold ${labelCls}`}>You're next up</div>
                <div className={`${isCoachingCtx ? "text-xl" : "text-3xl"} font-extrabold leading-tight mt-1`} data-testid="ss-next-up-label-text">
                  {data.me_next_up?.is_coaching
                    ? (data.me_next_up?.has_further_coaching
                        ? `COACHING · ${data.me_next_up?.court_name || "COURT"}`
                        : `COACHING · NEXT UP · ${data.me_next_up?.court_name || "COURT"}`)
                    : (data.me_next_up?.court_name || "Court")}
                </div>
                <div className="text-xs font-semibold mt-1 opacity-90">
                  {data.me_next_up?.state === "live"
                    ? `In about ${fmtMS(data.me_next_up?.remaining_seconds || 0)}`
                    : "Head over — you're up next"}
                </div>
              </>
            )}
          </div>
          );
        })()}

        {/* Iter229 → Iter314 — Welcome-Back hero retired. The
             GroupStatusBox at the top of the page now owns approve /
             reject for every play-request. This slot intentionally
             renders nothing. */}

        {/* Iter355 · Bug 4 — Coaching lock-in info panel. Renders for
             every coaching_locked player (holding_for_coaching set) so
             they see COACHING · court · time-until-start · signup
             mates in the same visual style as the on-court card.
             Iter356 · Bug 2 — Drop the on-court suppression so drag-
             drop players (who ARE on the court's current_player_ids)
             also render this panel. Backend already delivers mates
             union for those players. */}
        {/* Iter366 · Fix 2 — The separate "COACHING · LOCKED IN"
             block (previously rendered here) has been merged into
             the single Coaching card above (ss-holding-banner).
             This slot intentionally renders nothing. */}
        {null && data.holding_for_coaching && (
          <div data-testid="ss-coaching-locked-card-placeholder" />
        )}

        {/* Iter314 — Play-request GroupStatusBox. Replaces the iter190
             welcome-back hero + partner inbox + party invite chunks.
             Renders whenever the player has an active request (either
             as requester or as a still-pending / already-approved
             target). Rejecting hides the box on that player's screen. */}
        <HelperModeSwitch festivalId={id} token={token} verifyKey={verifyKey} />

        {activeRequest && (
          <GroupStatusBox
            req={activeRequest}
            festivalId={id}
            token={token}
            verifyKey={verifyKey}
            peers={peers}
            onChanged={load}
          />
        )}

        {/* Iter190 — Incoming partner requests. */}
        {/* ================================================================
             ⚠️ INTENTIONAL — THIS IS THE ONLY ACCEPT/DECLINE PROMPT
             PERMITTED IN THIS FILE. Do NOT add any other accept/decline
             prompts to this component. The court-invite / `ss-invite-card`
             pattern has been removed permanently as of iter270. If you
             ever find yourself about to add a similar Accept/Decline pair
             for anything OTHER than a direct partner request from another
             named player, STOP — the answer is no.
             (Regressed three times: iter265, iter267, iter269. Fixed for
             good in iter270.)
             Iter314 — Legacy `partner_requests_in` inbox retired. The
             GroupStatusBox above owns the approve/reject UX now. Left
             here as a dead render so any straggling backend payload
             (0 items after the wipe migration) doesn't crash the page.
             ================================================================ */}
        {false && (data.partner_requests_in || []).length > 0 && (
          <section className="mt-4" data-testid="ss-partner-inbox-legacy" />
        )}

        {/* Status block */}
        <section className="mt-6" data-testid="ss-status-section">
          <div className="label-mono text-[#CCFF00] mb-2">MY STATUS</div>
          <div className="text-xs text-[#A3A3A3] mb-3">Currently: <b className="text-white">{STATUS_DISPLAY_LABEL[player.status] || player.status || "unknown"}</b></div>
          {!checkedIn ? (
            <div className="border border-[#FFB020] bg-[#4B3F0F]/40 p-3 text-sm" data-testid="ss-not-checked-in">
              You aren't checked in yet. Please see the admin desk — self-service unlocks once they wave you through.
            </div>
          ) : (player.status === "on_court" || player.status === "assigned") ? (
            <div className={`border p-3 text-sm ${ui.warnBox}`} data-testid="ss-locked-note">
              You can't change status until your current game finishes. See the admin desk if anything's wrong.
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-2">
              {/* Iter268/271 — Queue-for-multiple-games selector.
                   Rendered above the Available button when the admin
                   toggle is ON AND max > 1. Options 1..maxGames only. */}
              {data.queue_multi_games_enabled && ((data.queue_multi_games_max || 1) > 1) && (
                <div className="border border-[#262626] bg-[#141414] p-3 flex items-center justify-between gap-3" data-testid="ss-queue-count-row">
                  <div>
                    <div className="text-xs font-semibold text-white">Queue for how many games?</div>
                    <div className="text-[10px] uppercase tracking-wider text-[#A3A3A3]">Applies when you go Available. Auto-decrements after each game.</div>
                    {data?.player?.queue_remaining > 1 && (
                      <div className="text-[10px] text-[#CCFF00] mt-1" data-testid="ss-queue-remaining">
                        {data.player.queue_remaining} games queued after this
                      </div>
                    )}
                  </div>
                  <div className="flex gap-1" data-testid="ss-queue-count-picker">
                    {Array.from({ length: Math.max(1, Math.min(4, Number(data.queue_multi_games_max) || 1)) }, (_, i) => i + 1).map((n) => (
                      <button
                        key={n}
                        type="button"
                        data-testid={`ss-queue-count-${n}`}
                        onClick={() => setQueueCount(n)}
                        className={`w-8 h-8 text-sm font-bold border ${queueCount === n ? "bg-[#CCFF00] text-black border-[#CCFF00]" : "bg-transparent border-[#262626] text-white hover:border-[#CCFF00]"}`}
                      >{n}</button>
                    ))}
                  </div>
                </div>
              )}
              {["available", "resting", "inactive"].map((s) => {
                const meta = STATUS_META[s];
                // Iter402 · Fix 3 — Treat `arrived` (checked-in / sin-bin
                // release / done-for-now reset) as Resting to match
                // Live Ops. Prior bug: self-service showed Available/
                // Green after any of the three reinstate paths even
                // though the OPS side displayed Resting. The player
                // must now explicitly tap Available when ready.
                const active = player.status === s || (s === "resting" && player.status === "arrived");
                // Iter401 — Next-Up queue protection. When the player
                // is queued on any court's next_up_player_ids the
                // backend rejects Resting / Done for now with 409;
                // dim the buttons here so the user sees the block up
                // front. Available stays selectable.
                const queuedBlock = player.queued_next_up && (s === "resting" || s === "inactive");
                return (
                  <button
                    key={s}
                    type="button"
                    disabled={busy || queuedBlock}
                    onClick={() => setStatus(s)}
                    data-testid={`ss-status-${s}`}
                    className={`flex items-center gap-3 px-4 py-5 border-2 text-left ${queuedBlock ? "opacity-40 cursor-not-allowed" : ""} ${active ? meta.cls : meta.idle}`}
                  >
                    {meta.icon}
                    <div className="flex-1">
                      <div className="text-lg font-bold leading-tight">{meta.label}</div>
                      <div className="text-sm mt-0.5 opacity-90 leading-snug">
                        {s === "available" && "Ready to play the next round"}
                        {s === "resting" && "Skip me — press Available when you're ready"}
                        {s === "inactive" && "Locks you out for the day — admin only reset"}
                      </div>
                    </div>
                    {active && <Check className="w-5 h-5" />}
                  </button>
                );
              })}
              {player.queued_next_up && (
                <div
                  className="text-sm text-[#FFB020] mt-1 leading-snug"
                  data-testid="ss-status-queued-hint"
                >
                  You've been queued for the next game — speak to your organiser if you need to sit out.
                </div>
              )}
            </div>
          )}
        </section>

        {/* Partner requests block */}
        <section className="mt-8" data-testid="ss-partners-section">
          <div className="label-mono text-[#CCFF00] mb-2 flex items-center gap-1">
            <Users className="w-3.5 h-3.5" /> PLAY WITH ({(player.preferred_partner_ids || []).length}/3)
          </div>
          {checkedIn && partner_editing_enabled && (
            <div className={`text-[10px] ${ui.muted} mb-2 leading-tight`} data-testid="ss-partner-mode-hint">
              Pick <b>1</b> = pair (2 randoms fill the court) · <b>2–3</b> = team (goes on the same court)
            </div>
          )}
          {!checkedIn ? (
            <div className="border border-[#262626] bg-[#141414] p-3 text-xs text-[#A3A3A3]" data-testid="ss-partners-locked-checkin">
              Available once the admin desk has checked you in.
            </div>
          ) : !partner_editing_enabled ? (
            <div className="border border-[#262626] bg-[#141414] p-3 text-xs text-[#A3A3A3]" data-testid="ss-partners-disabled">
              Partner requests open when the organiser switches to <b className="text-white">All-In</b> mode.
            </div>
          ) : (
            <PartnerPicker
              festivalId={id}
              token={token}
              verifyKey={verifyKey}
              player={player}
              peers={peers}
              cooldownLeft={cooldownLeft}
              activeRequest={activeRequest}
              onSaved={load}
            />
          )}
        </section>

        <SelfServiceEmailLink festivalId={id} token={token} verifyKey={verifyKey} isLight={isLight} />

        {/* Iter190 — Compact courts view (like the wall, on a phone).
             Iter268 — Show/Hide toggle. Preference does not persist. */}
        {(data.all_courts || []).length > 0 && (
          <section className="mt-6" data-testid="ss-courts-view">
            <div className="flex items-center justify-between mb-2">
              <div className="label-mono text-[#CCFF00]">ALL COURTS</div>
              <button
                type="button"
                data-testid="ss-courts-toggle"
                onClick={() => setShowCourts((v) => !v)}
                className={`text-[10px] uppercase tracking-wider px-2 py-1 border ${ui.subtleBorder} ${isLight ? "text-[#0A0A0A]" : ""} hover:border-[#CCFF00] hover:text-[#CCFF00]`}
              >
                {showCourts ? "Hide Courts" : "Show Courts"}
              </button>
            </div>
            {showCourts && (
              <div className="space-y-2">
                {data.all_courts.map((c) => (
                  <div key={c.id} className={`border ${ui.subtleBorder} p-2 text-xs`} data-testid={`ss-court-${c.id}`}>
                    <div className="flex justify-between items-baseline gap-2">
                      <span className="font-semibold truncate">{c.name}</span>
                      <span className={`text-[10px] uppercase tracking-wider ${ui.muted} shrink-0`}>{c.state}</span>
                    </div>
                    {(c.current_players || []).length > 0 && (
                      // Iter365 · Fix 2 — Player names on the phone
                      // wall must wrap and stay legible at 390px. The
                      // old single-line paragraph clipped anyone with
                      // a name longer than the tiny column. Iter365 ·
                      // Fix 3 — Light-mode readability: swap the fixed
                      // `text-[#A3A3A3]` for a bright-enough shade
                      // via the theme-scoped `ui.muted`.
                      <div className={`text-[11px] ${isLight ? "text-[#0A0A0A]" : "text-white"} mt-0.5 break-words leading-snug`}>{c.current_players.join(" · ")}</div>
                    )}
                    {(c.next_up_players || []).length > 0 && (
                      <div className={`text-[10px] ${ui.muted} break-words leading-snug`}>Next: {c.next_up_players.join(", ")}</div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </section>
        )}

        {/* Iter268 — Refresh Profile: nuclear reset. Blocked on
             sin-bin / live-or-pre-start court by the backend. */}
        <button
          type="button"
          data-testid="ss-refresh-profile"
          onClick={async () => {
            if (!window.confirm("Reset your profile? This clears all team links, cancels every partner request, and sets you back to Available.")) return;
            try {
              await axios.post(`${BACKEND}/api/festival/${id}/self-service/${token}/profile/refresh${verifyKey ? `?vk=${encodeURIComponent(verifyKey)}` : ""}`);
              toast.success("Profile refreshed — you're Available");
              load();
            } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't refresh"); }
          }}
          className="mt-4 w-full py-3 border border-[#FF4500] text-xs uppercase tracking-wider text-[#FF4500] hover:bg-[#FF4500] hover:text-black inline-flex items-center justify-center gap-2"
        >
          Refresh Profile · reset team links & pending requests
        </button>

        <div className={`mt-4 text-[10px] ${ui.muted} text-center uppercase tracking-widest`}>
          Bookmark this page · no login required
        </div>
        {data.sponsor_ribbon && (
          <SponsorZoneMobile
            list={data.sponsor_ribbon.footer || []}
            rotate={Math.max(3, Math.min(120, data.sponsor_ribbon.rotate_seconds || 60))}
            zone="footer"
            ui={ui}
          />
        )}
      </div>
    </div>
  );
}

/* Iter301/302/313 — Mobile sponsor zone. Independent instance per zone
 * (header + footer); the header one is rendered above the main content
 * card, the footer one below. Both rotate every ~60s with a 500ms fade. */
function ssClassifyContact(raw) {
  if (!raw) return null;
  const s = String(raw).trim();
  if (!s) return null;
  if (/^https?:\/\//i.test(s)) return { kind: "url", href: s, label: s.replace(/^https?:\/\//i, "") };
  if (/^[a-z0-9-]+(\.[a-z0-9-]+)+(\/[^\s]*)?$/i.test(s)) return { kind: "url", href: `https://${s}`, label: s };
  if (/^(\+?\d[\d\s()-]{6,})$/.test(s)) return { kind: "tel", href: `tel:${s.replace(/\s+/g, "")}`, label: s };
  return { kind: "note", href: null, label: s };
}

function ssResolveLogo(raw) {
  if (!raw) return null;
  const s = String(raw).trim();
  if (!s) return null;
  if (/^https?:\/\//i.test(s)) return s;
  if (s.startsWith("/api/")) return `${BACKEND}${s}`;
  if (s.startsWith("/uploads/")) return `${BACKEND}/api${s}`;
  if (s.startsWith("/")) return `${BACKEND}${s}`;
  return s;
}

function SponsorZoneMobile({ list, rotate, zone, ui }) {
  // Iter313.1 — Mobile group size capped at 2 (screens narrower than
  // ~420px look cramped with 3 mini-cards). Same auto rule otherwise:
  // 1 solo, 2 side-by-side, 3+ rotate in groups of 2. Whole page fades
  // together so the layout doesn't jitter.
  const GROUP = 2;
  const pages = list.length <= GROUP
    ? [list]
    : Array.from({ length: Math.ceil(list.length / GROUP) }, (_, i) => list.slice(i * GROUP, i * GROUP + GROUP));
  const [pageIdx, setPageIdx] = useState(0);
  const [fade, setFade] = useState("in");
  useEffect(() => {
    if (pages.length <= 1) return undefined;
    const cadence = Math.max(3, rotate || 60) * 1000;
    const t = setInterval(() => {
      setFade("out");
      setTimeout(() => {
        setPageIdx((i) => (i + 1) % pages.length);
        setFade("in");
      }, 500);
    }, cadence);
    return () => clearInterval(t);
  }, [pages.length, rotate]);
  if (!list.length) return null;
  const active = pages[pageIdx % pages.length];
  const marginCls = zone === "header" ? "mb-4" : "mt-6";
  const boxCls = `${marginCls} border ${ui?.subtleBorder || "border-[#262626]"} bg-transparent px-3 py-3 text-center transition-opacity duration-500 ${fade === "out" ? "opacity-0" : "opacity-100"}`;
  return (
    <div
      className={boxCls}
      data-testid={`ss-sponsor-${zone}`}
      data-position={zone}
      data-count={list.length}
      data-page-count={pages.length}
    >
      <div className="text-[9px] uppercase tracking-[0.3em] text-[#CCFF00]">Proudly supported by</div>
      <div className="mt-2 flex items-stretch justify-around gap-3 min-w-0">
        {active.map((s) => <SponsorCardMobile key={s.id} sponsor={s} zone={zone} />)}
      </div>
      {pages.length > 1 && (
        <div className="mt-2 flex items-center justify-center gap-1" data-testid={`ss-sponsor-${zone}-dots`}>
          {pages.map((_, i) => (
            <span
              key={i}
              className={`w-1.5 h-1.5 rounded-full ${i === (pageIdx % pages.length) ? "bg-[#CCFF00]" : "bg-[#262626]"}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/* Iter313.1 — Mobile sponsor card. Extracted so a zone can render two
 * side-by-side without duplicating markup. Slimmer than the desktop
 * variant so two cards fit inside the mobile self-service column. */
function SponsorCardMobile({ sponsor, zone }) {
  const contact = ssClassifyContact(sponsor.contact || sponsor.link_url);
  const logoSrc = ssResolveLogo(sponsor.logo_url);
  const inner = (
    <div className="flex flex-col items-center gap-1 min-w-0">
      {logoSrc && (
        <img
          src={logoSrc}
          alt={sponsor.name}
          className="h-10 max-w-[110px] object-contain bg-white p-1 rounded"
          onError={(e) => { e.currentTarget.style.display = "none"; }}
        />
      )}
      <div className="min-w-0 text-center">
        <div className="text-xs font-semibold truncate text-white">{sponsor.name}</div>
        {contact && (
          <div className="text-[10px] text-[#A3A3A3] truncate">{contact.label}</div>
        )}
      </div>
    </div>
  );
  if (contact?.href) {
    return (
      <a
        href={contact.href}
        target={contact.kind === "url" ? "_blank" : undefined}
        rel="noopener noreferrer"
        className="flex-1 min-w-0 hover:opacity-80 transition-opacity"
        data-testid={`ss-sponsor-${zone}-card-${sponsor.id}`}
      >
        {inner}
      </a>
    );
  }
  return (
    <div className="flex-1 min-w-0" data-testid={`ss-sponsor-${zone}-card-${sponsor.id}`}>
      {inner}
    </div>
  );
}

function PartnerPicker({ festivalId, token, verifyKey, player, peers, cooldownLeft, activeRequest, onSaved }) {
  const [selected, setSelected] = useState(new Set(player.preferred_partner_ids || []));
  const [saving, setSaving] = useState(false);
  const initial = useMemo(() => new Set(player.preferred_partner_ids || []), [player.preferred_partner_ids]);
  // Iter272 — DO NOT blanket-resync `selected` on every poll refresh.
  // The parent re-renders every 3-5s with a fresh `player` reference,
  // and the old effect
  //     useEffect(() => setSelected(...), [player.preferred_partner_ids])
  // wiped in-progress ticks before the user could hit Save.
  //
  // Instead, only resync from the server when the local selection
  // exactly matches the previous server state (i.e., the user has
  // NOT modified their picks yet). If they've changed anything, we
  // preserve their ticks until Save or an explicit untick. After a
  // successful save `onSaved` reloads the parent — at that point the
  // server-side prefs match `selected` so this effect is a no-op.
  const lastServerRef = useRef(null);
  // Iter386 v2 — content-based dep. React uses `Object.is` reference
  // equality on deps and, while axios normally produces fresh arrays
  // per poll, we use a JSON-stringified fingerprint to be completely
  // immune to any upstream reference-stability optimisation. This
  // guarantees the effect fires the moment the SERVER content
  // changes — including the non-empty → empty transition that
  // signals a post-game bubble dissolution.
  const serverPrefsFingerprint = useMemo(
    () => JSON.stringify([...(player.preferred_partner_ids || [])].sort()),
    [player.preferred_partner_ids],
  );
  useEffect(() => {
    const server = new Set(player.preferred_partner_ids || []);
    const prevServer = lastServerRef.current;
    // First render — just record the baseline and skip.
    if (prevServer === null) {
      lastServerRef.current = server;
      return;
    }
    // Iter386 · Fix — When the server transitions from a non-empty
    // preferred_partner_ids to an empty set (the signature of a
    // post-game bubble dissolution), force-reset the local selection
    // regardless of any dirty local edit. Per user spec: "no
    // previous selections should persist after dissolution."
    if (prevServer.size > 0 && server.size === 0) {
      setSelected(new Set());
      lastServerRef.current = server;
      return;
    }
    // Compare current LOCAL `selected` with the PREVIOUS server snapshot
    // (i.e., pre-poll). If they match, the user has no pending edits →
    // safe to adopt the new server state. Otherwise leave `selected`
    // untouched so the ticks survive the background refresh.
    let inSync = selected.size === prevServer.size;
    if (inSync) {
      for (const s of selected) { if (!prevServer.has(s)) { inSync = false; break; } }
    }
    if (inSync) {
      setSelected(server);
    }
    lastServerRef.current = server;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serverPrefsFingerprint]);

  // Iter386 v2 · Fix — Defence-in-depth: also detect the parent's
  // `activeRequest` transitioning from truthy → falsy (the classic
  // dissolution / completion signal) and force-resync `selected` to
  // whatever the server now says (which is `[]` after the backend
  // clears prefs in dissolve_play_request_for_court). This covers
  // the edge case where the server prefs and local `selected` had
  // already been resynced to the same empty set before the poll
  // that carries the activeRequest=null transition arrives.
  const prevActiveReqRef = useRef(!!activeRequest);
  useEffect(() => {
    const prev = prevActiveReqRef.current;
    const now = !!activeRequest;
    if (prev && !now) {
      setSelected(new Set(player.preferred_partner_ids || []));
    }
    prevActiveReqRef.current = now;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeRequest]);

  // Iter214 — Lock the picker once a save has been made until the
  // player finishes a court (`last_court_exit_at` advances past
  // `partners_saved_at`).
  // Iter378 · Fix 3 — ALSO lock whenever an activeRequest exists
  // for this viewer (they're the requester of a pending/locked
  // group), because the new play-request POST doesn't stamp
  // `partners_saved_at` on the requester doc. Edits happen through
  // the GroupStatusBox Edit button instead.
  const savedAt = player.partners_saved_at;
  const lastExit = player.last_court_exit_at;
  const savedLock = !!savedAt && (!lastExit || lastExit <= savedAt);
  const requestLock = !!activeRequest && activeRequest.viewer_id === activeRequest.requester_id;
  const locked = savedLock || requestLock;

  const toggle = (pid) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(pid)) {
        if (cooldownLeft > 0 && initial.has(pid)) {
          toast.warning(`Cooldown — you can only remove partners after ${fmtMS(cooldownLeft)}`);
          return prev;
        }
        next.delete(pid);
      } else {
        if (next.size >= 3) {
          toast.info("Up to 3 partners — you're the 4th player");
          return prev;
        }
        next.add(pid);
      }
      return next;
    });
  };

  const dirty = useMemo(() => {
    if (selected.size !== initial.size) return true;
    for (const s of selected) if (!initial.has(s)) return true;
    return false;
  }, [selected, initial]);

  const save = async () => {
    setSaving(true);
    try {
      // Iter314 — Rewired to the new play-request engine. The picker's
      // ticks become a single POST that creates the request + fans out
      // notifications; the box on-screen switches to GroupStatusBox on
      // the next poll (or the immediate onSaved refresh).
      const targets = Array.from(selected);
      if (targets.length < 1) {
        toast.info("Pick at least one player to request");
        setSaving(false);
        return;
      }
      const resp = await axios.post(
        `${BACKEND}/api/festival/${festivalId}/self-service/${token}/play-requests${verifyKey ? `?vk=${encodeURIComponent(verifyKey)}` : ""}`,
        { target_ids: targets },
      );
      toast.success(
        targets.length === 1 ? "Request sent — waiting for their reply"
                             : `Request sent to ${targets.length} players`,
      );
      // Iter381 · Fix 2 — Backend surfaces `hold_notice` on the
      // response when any target is currently on-court. Show it as
      // a follow-up toast so the requester understands the 30-min
      // hold rule immediately (was previously written to
      // festival_notifications but never surfaced in the UI).
      if (resp?.data?.hold_notice) {
        toast.info(resp.data.hold_notice, { duration: 8000 });
      }
      await onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't send request");
    } finally { setSaving(false); }
  };

  return (
    <div>
      {locked && (
        <div className="mb-3 border border-[#66C2FF] bg-[#0F1E2E] p-2 text-[11px]" data-testid="ss-partner-locked">
          {requestLock
            ? "Play request live — use Edit request above to change the group."
            : "Partners locked in — you can pick again after your next game."}
        </div>
      )}
      {cooldownLeft > 0 && !locked && (
        <div className="mb-3 border border-[#FFB020] bg-[#4B3F0F]/40 p-2 text-[11px]" data-testid="ss-partner-cooldown">
          Just off court — you can add partners now, but removing one waits {fmtMS(cooldownLeft)}.
        </div>
      )}
      <div className={`space-y-1 max-h-[45vh] overflow-y-auto border border-[#262626] p-2 ${locked ? "opacity-40 pointer-events-none" : ""}`}>
        {peers.length === 0 && (
          <div className="text-xs text-[#525252] italic p-2">No other players active yet.</div>
        )}
        {peers.map((p) => {
          const on = selected.has(p.id);
          // Iter229 — busy peers (on-court/assigned) get an amber ring
          // + a "🎾 Playing" chip so senders know the request will queue.
          const isBusy = p.status === "on_court" || p.status === "assigned";
          return (
            <button
              key={p.id}
              type="button"
              onClick={() => toggle(p.id)}
              data-testid={`ss-peer-${p.id}`}
              data-status={p.status}
              className={`w-full flex items-center gap-2 px-3 py-2 border text-left ${on ? "border-[#CCFF00] bg-[#0F0F0A]" : isBusy ? "border-[#FFB020]/60 hover:border-[#FFB020]" : "border-transparent hover:border-[#262626]"}`}
            >
              <div className={`w-4 h-4 border ${on ? "bg-[#CCFF00] border-[#CCFF00]" : "border-[#525252]"} flex items-center justify-center`}>
                {on && <Check className="w-3 h-3 text-black" />}
              </div>
              <span className="flex-1 text-sm">{p.first_name} {p.last_name}</span>
              {isBusy && (
                <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 border border-[#FFB020] text-[#FFB020]" title="Currently playing — request will queue until they come off">
                  🎾 Playing
                </span>
              )}
              {p.colour_band && (
                <span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 border ${BAND_CHIP[p.colour_band] || "border-[#262626]"}`}>
                  {p.colour_band}
                </span>
              )}
            </button>
          );
        })}
      </div>
      <button type="button" disabled={!dirty || saving || locked} onClick={save} data-testid="ss-partners-save" className="mt-3 w-full py-3 font-bold text-xs uppercase tracking-wider disabled:opacity-40" style={{ background: "#CCFF00", color: "#0A0A0A" }}>
        {locked ? "Locked until next game" : saving ? "Saving…" : dirty ? `Save ${selected.size} partner${selected.size === 1 ? "" : "s"}` : "No changes"}
      </button>
    </div>
  );
}

/* Iter314 — Play-request GroupStatusBox.
 *
 * Renders the current active request from the viewer's perspective:
 *   · Pending target → full proposed group + Approve / Reject buttons.
 *   · Approved      → live status view (green tick / waiting / ON COURT).
 *   · Rejecter      → unmounted upstream (parent sets activeRequest=null).
 *
 * Owns nothing beyond render + click → POST; parent polls
 * /play-requests/mine on every load() so state stays fresh.
 */
function GroupStatusBox({ req, festivalId, token, verifyKey, peers, onChanged }) {
  const [busy, setBusy] = useState(false);
  // Iter378 · Fix 1/3 — inline checkbox-list edit mode replaces the
  // Iter315 window.prompt entirely. `editing` toggles the view to
  // the picker; `editSelected` is the working set of target ids.
  const [editing, setEditing] = useState(false);
  const [editSelected, setEditSelected] = useState(() =>
    new Set((req.target_ids || []))
  );
  const viewerIsPending = req.viewer_response === "pending";
  const viewerIsRequester = req.viewer_id === req.requester_id;

  // Iter391 · Follow-up (v2) — TRUE client-side countdown for the
  // 30-min partner-hold. The previous version re-derived `expiresMs`
  // on every parent poll (~5s) which visually looked like the timer
  // was tied to the fetch cadence. This version:
  //   1. Anchors the server-issued expiry on first mount (state, not
  //      a raw prop derivation) so subsequent polls of the same
  //      `req.expires_at` are no-ops.
  //   2. Runs a plain `setInterval(1000)` under `useEffect(() => …, [])`
  //      so the ticker never restarts on prop / poll churn — it
  //      lives for the mounted lifetime of the card.
  //   3. Only accepts a new server value when the ISO string actually
  //      changes (e.g., organiser extends the hold), in which case
  //      the anchor is updated but the ticker keeps ticking.
  const [expiresMs, setExpiresMs] = useState(() =>
    req.expires_at ? new Date(req.expires_at).getTime() : null
  );
  useEffect(() => {
    const next = req.expires_at ? new Date(req.expires_at).getTime() : null;
    setExpiresMs((prev) => (prev === next ? prev : next));
  }, [req.expires_at]);
  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  const holdSecondsLeft = expiresMs
    ? Math.max(0, Math.ceil((expiresMs - nowMs) / 1000))
    : 0;

  // Iter378 · Fix 3 — When the request payload refreshes (poll), keep
  // the working set in sync ONLY when the user hasn't started editing.
  useEffect(() => {
    if (!editing) setEditSelected(new Set(req.target_ids || []));
  }, [req.target_ids, editing]);

  const respond = async (action) => {
    setBusy(true);
    try {
      const url = `${BACKEND}/api/festival/${festivalId}/self-service/${token}/play-requests/${req.id}/respond${verifyKey ? `?vk=${encodeURIComponent(verifyKey)}` : ""}`;
      const r = await axios.post(url, { action });
      const status = r?.data?.request?.status;
      if (action === "approve") {
        toast.success(status === "locked" ? "Group locked — waiting for a court" : "Approved");
      } else {
        toast.info("Request declined");
      }
      await onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't respond");
    } finally { setBusy(false); }
  };

  // Iter378 · Fix 1/2/3 — Save edited target list via the checkbox
  // picker. Direct ids (no name resolution), so a silent name-match
  // failure is impossible. Empty selection = cancel/dissolve the
  // request (backend requires >=1 target, so we surface a clear
  // toast rather than PATCH into an error).
  const saveEdits = async () => {
    const target_ids = Array.from(editSelected);
    if (target_ids.length < 1) {
      toast.error("Pick at least one player — or press Cancel to keep the current group");
      return;
    }
    setBusy(true);
    try {
      const url = `${BACKEND}/api/festival/${festivalId}/self-service/${token}/play-requests/${req.id}${verifyKey ? `?vk=${encodeURIComponent(verifyKey)}` : ""}`;
      await axios.patch(url, { target_ids });
      toast.success("Request updated");
      setEditing(false);
      await onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't edit");
    } finally { setBusy(false); }
  };
  const startEdit = () => {
    setEditSelected(new Set(req.target_ids || []));
    setEditing(true);
  };
  const cancelEdit = () => {
    setEditSelected(new Set(req.target_ids || []));
    setEditing(false);
  };

  const bandChip = (band) => ({
    red: "border-[#FF4500] text-[#FF4500]",
    blue: "border-[#66C2FF] text-[#66C2FF]",
    green: "border-[#4CAF50] text-[#4CAF50]",
    unrated: "border-[#525252] text-[#525252]",
  })[band] || "border-[#525252] text-[#525252]";

  const statusChip = (m) => {
    if (m.on_court) return { label: "ON COURT", cls: "bg-[#FFB020] text-black" };
    if (m.response === "approved") return { label: "✓", cls: "bg-[#CCFF00] text-black" };
    if (m.response === "rejected") return { label: "REJECTED", cls: "bg-[#4B0F0F] text-[#FF4500]" };
    return { label: "WAITING", cls: "bg-[#262626] text-[#A3A3A3]" };
  };

  const border = req.status === "locked" ? "border-[#CCFF00]"
                : req.status === "live" ? "border-[#66C2FF]"
                : "border-[#FFB020]";
  const heading = viewerIsPending
    ? "Play request — you've been invited"
    : req.status === "locked"
      ? "Group locked — waiting for a court"
      : req.status === "live"
        ? "Your group is on court"
        : viewerIsRequester
          ? "Waiting on your friends"
          : "Waiting on the rest of the group";

  return (
    <section className={`mt-4 border-2 ${border} bg-[#141414] p-4`} data-testid="ss-group-status-box" data-status={req.status}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="text-[10px] uppercase tracking-widest font-bold text-[#CCFF00]">{heading}</div>
        {/* Iter315 — Editable / Locked badge for the requester. */}
        {viewerIsRequester && req.status === "pending" && (
          req.editable ? (
            <span className="text-[9px] uppercase tracking-widest px-2 py-0.5 border border-[#66C2FF] text-[#66C2FF]" data-testid="ss-group-editable-badge">Editable</span>
          ) : (
            <span className="text-[9px] uppercase tracking-widest px-2 py-0.5 border border-[#FFB020] text-[#FFB020] bg-[#4B3F0F]/40" data-testid="ss-group-locked-badge">Locked — someone approved</span>
          )
        )}
      </div>
      {req.assigned_by_staff && (
        <div className="mt-2 border border-[#CCFF00]/60 bg-[#1A2200] px-3 py-2 text-[12px] text-white" data-testid="ss-staff-paired-notice">
          Festival staff have paired you with this group. You'll be seated together when a court is free.
        </div>
      )}
      <div className="mt-3 space-y-2">
        {req.members.map((m) => {
          const chip = statusChip(m);
          return (
            <div key={m.player_id} className="flex items-center justify-between gap-2 border border-[#262626] px-3 py-2" data-testid={`ss-group-member-${m.player_id}`}>
              <div className="flex items-center gap-2 min-w-0">
                <span className={`w-6 h-6 rounded-full border ${bandChip(m.colour_band)} flex items-center justify-center text-[9px] font-bold`}>
                  {(m.colour_band || "u")[0].toUpperCase()}
                </span>
                <span className="text-sm truncate text-white">
                  {m.first_name} {m.last_name}
                  {m.is_requester && <span className="ml-1 text-[9px] text-[#66C2FF] uppercase">requester</span>}
                </span>
              </div>
              <span className={`px-2 py-0.5 text-[10px] uppercase tracking-widest font-bold ${chip.cls}`} data-testid={`ss-group-member-${m.player_id}-status`}>
                {chip.label}
              </span>
            </div>
          );
        })}
      </div>

      {viewerIsPending && req.status === "pending" && (
        <div className="mt-3 flex gap-2">
          <button
            type="button"
            data-testid="ss-group-approve"
            disabled={busy}
            onClick={() => respond("approve")}
            className="flex-1 py-3 bg-[#CCFF00] text-black font-bold text-sm uppercase tracking-wider disabled:opacity-50"
          >
            Approve
          </button>
          <button
            type="button"
            data-testid="ss-group-reject"
            disabled={busy}
            onClick={() => respond("reject")}
            className="flex-1 py-3 border border-[#FF4500] text-[#FF4500] font-bold text-sm uppercase tracking-wider disabled:opacity-50"
          >
            Reject
          </button>
        </div>
      )}

      {req.status === "pending" && !viewerIsPending && req.expires_at && (
        <div className="mt-3 text-[10px] uppercase tracking-widest text-[#A3A3A3]" data-testid="ss-group-expiry">
          Auto-locks with whoever approves at {new Date(req.expires_at).toLocaleTimeString()}
          {holdSecondsLeft > 0 && (
            <>
              {" · "}
              <b className="text-[#FFB020] tabular-nums" data-testid="ss-group-hold-countdown">
                {fmtMS(holdSecondsLeft)} left
              </b>
            </>
          )}
        </div>
      )}
      {/* Iter386 · Follow-up — Pending-target viewers see the same live
           30-minute hold countdown so they know how long until the
           bubble auto-locks or dissolves. Requester side (above) also
           gets the live tick. Both branches tick every 1s from the
           shared `nowMs` state at the top of the component. */}
      {req.status === "pending" && viewerIsPending && req.expires_at && holdSecondsLeft > 0 && (
        <div className="mt-3 text-[10px] uppercase tracking-widest text-[#A3A3A3]" data-testid="ss-group-expiry-pending">
          Hold expires in{" "}
          <b className="text-[#FFB020] tabular-nums" data-testid="ss-group-hold-countdown-pending">
            {fmtMS(holdSecondsLeft)}
          </b>
        </div>
      )}
      {/* Iter378 · Fix 1/3 — Inline checkbox-list edit mode replaces
           the Iter315 window.prompt. Members who've responded are
           greyed out and unclickable; only "pending" targets can be
           dropped and any peer not already in the group can be
           added (capped at 3). This block only renders when the
           requester actively pressed Edit AND the request is still
           editable. */}
      {viewerIsRequester && editing && req.editable && (
        <div className="mt-3 border border-[#66C2FF] bg-[#0F1E2E] p-3" data-testid="ss-group-edit-picker">
          <div className="text-[10px] uppercase tracking-widest text-[#66C2FF] mb-2">
            EDIT REQUEST · TICK / UNTICK BELOW
          </div>
          <div className="space-y-1 max-h-[35vh] overflow-y-auto border border-[#262626] p-2">
            {/* Members already in the request. */}
            {req.members.filter((m) => !m.is_requester).map((m) => {
              const locked = m.response !== "pending"; // approved / rejected
              const on = editSelected.has(m.player_id);
              return (
                <button
                  key={m.player_id}
                  type="button"
                  disabled={locked}
                  onClick={() => setEditSelected((prev) => {
                    const n = new Set(prev);
                    if (n.has(m.player_id)) n.delete(m.player_id);
                    else if (n.size < 3) n.add(m.player_id);
                    return n;
                  })}
                  data-testid={`ss-edit-member-${m.player_id}`}
                  data-locked={locked ? "1" : "0"}
                  className={`w-full flex items-center gap-2 px-3 py-2 border text-left ${locked ? "opacity-40 pointer-events-none border-[#262626]" : on ? "border-[#CCFF00] bg-[#0F0F0A]" : "border-transparent hover:border-[#262626]"}`}
                >
                  <div className={`w-4 h-4 border ${on ? "bg-[#CCFF00] border-[#CCFF00]" : "border-[#525252]"} flex items-center justify-center`}>
                    {on && <Check className="w-3 h-3 text-black" />}
                  </div>
                  <span className="flex-1 text-sm">{m.first_name} {m.last_name}</span>
                  {locked && (
                    <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 border border-[#FFB020] text-[#FFB020]">
                      {m.response === "approved" ? "Approved" : "Declined"}
                    </span>
                  )}
                </button>
              );
            })}
            {/* Peers who aren't in the request yet (available to add). */}
            {(peers || []).filter((p) => !(req.target_ids || []).includes(p.id) && p.id !== req.requester_id).map((p) => {
              const on = editSelected.has(p.id);
              return (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => setEditSelected((prev) => {
                    const n = new Set(prev);
                    if (n.has(p.id)) n.delete(p.id);
                    else if (n.size < 3) { n.add(p.id); }
                    return n;
                  })}
                  data-testid={`ss-edit-peer-${p.id}`}
                  className={`w-full flex items-center gap-2 px-3 py-2 border text-left ${on ? "border-[#CCFF00] bg-[#0F0F0A]" : "border-transparent hover:border-[#262626]"}`}
                >
                  <div className={`w-4 h-4 border ${on ? "bg-[#CCFF00] border-[#CCFF00]" : "border-[#525252]"} flex items-center justify-center`}>
                    {on && <Check className="w-3 h-3 text-black" />}
                  </div>
                  <span className="flex-1 text-sm">{p.first_name} {p.last_name}</span>
                </button>
              );
            })}
          </div>
          <div className="mt-3 flex gap-2">
            <button
              type="button"
              onClick={saveEdits}
              disabled={busy}
              data-testid="ss-edit-save"
              className="flex-1 py-2 text-xs uppercase tracking-widest font-bold bg-[#CCFF00] text-black disabled:opacity-40"
            >
              {busy ? "Saving…" : "Save changes"}
            </button>
            <button
              type="button"
              onClick={cancelEdit}
              disabled={busy}
              data-testid="ss-edit-cancel"
              className="flex-1 py-2 text-xs uppercase tracking-widest border border-[#525252] text-[#A3A3A3] hover:border-white disabled:opacity-40"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Iter315 — Requester's Edit button. Only rendered while the
           request is still editable (pending + no non-requester has
           responded — Iter378 · Fix 4). Once anyone responds this
           button disappears entirely and the system takes over
           resolution via the 30-minute expiry pipeline. */}
      {viewerIsRequester && req.status === "pending" && req.editable && !editing && (
        <button
          type="button"
          onClick={startEdit}
          disabled={busy}
          data-testid="ss-group-edit"
          className="mt-3 w-full py-2 text-xs uppercase tracking-wider border border-[#66C2FF] text-[#66C2FF] hover:bg-[#66C2FF] hover:text-black disabled:opacity-40"
        >
          Edit request
        </button>
      )}
      {req.status === "locked" && (
        <div className="mt-3 text-[10px] text-[#A3A3A3]" data-testid="ss-group-locked-note">
          You'll be placed on court together as soon as everyone's free.
        </div>
      )}
    </section>
  );
}

